import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, Tag, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { base44 } from '@/api/base44Client';
import { useToast } from '@/components/ui/use-toast';
import EbayFindingCard from './EbayFindingCard';
import EbayDismissedFindings from './EbayDismissedFindings';

const money = (n) => `£${(Number(n) || 0).toFixed(2)}`;
const day = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB') : '—');

// Shows the eBay sales the app holds next to the records that look like the
// same sale filed elsewhere, with the correcting action inline. Read-only until
// an action is pressed — nothing is changed by opening this panel.
export default function EbaySalesReconciliationPanel() {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await base44.functions.invoke('ebaySalesAudit', {});
      if (res.data?.error) throw new Error(res.data.error);
      setAudit(res.data);
    } catch (e) {
      toast({
        variant: 'destructive',
        description: e?.response?.data?.error || e.message || 'Could not check the eBay sales.',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const repair = async (action, orderId, extra = {}) => {
    setBusy(`${orderId}${action}`);
    try {
      const res = await base44.functions.invoke('ebaySalesRepair', {
        action,
        order_id: orderId,
        ...extra,
      });
      if (res.data?.error) throw new Error(res.data.error);
      toast({ description: res.data.message });
      await load();
    } catch (e) {
      toast({
        variant: 'destructive',
        description: e?.response?.data?.error || e.message || 'That correction failed.',
      });
    } finally {
      setBusy(null);
    }
  };

  const findings = audit?.findings || [];
  const retag = audit?.retagCandidates || [];
  const dismissals = audit?.dismissals || [];

  return (
    <div className="bg-[#111115] border border-white/10 p-6">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
            Sales Reconciliation
          </h3>
          <p className="text-xs text-white/50 mt-1">
            Compares the eBay sales the app holds against records filed under other sources, so a
            sale that was imported twice — or filed under the wrong buyer — can be put right. A
            record already posted to Sage is never changed here.
          </p>
        </div>
        <Button
          onClick={load}
          disabled={loading}
          variant="outline"
          className="bg-transparent border-white/10 text-white/70 hover:bg-white/5"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          Check again
        </Button>
      </div>

      {loading && !audit ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-5 h-5 text-primary animate-spin" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
            {[
              ['eBay sales held', audit?.counts?.ebay_orders ?? 0],
              ['Duplicates', audit?.counts?.duplicates ?? 0],
              ['Need checking', audit?.counts?.possible_matches ?? 0],
              ['Ready to re-file', audit?.counts?.retag_candidates ?? 0],
            ].map(([label, value]) => (
              <div key={label} className="border border-white/10 p-3">
                <div className="text-[11px] text-white/40 uppercase tracking-wider">{label}</div>
                <div className="text-white text-sm font-medium">{value}</div>
              </div>
            ))}
          </div>

          {findings.length === 0 && retag.length === 0 ? (
            <p className="text-xs text-white/50 mt-5 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              Nothing looks duplicated or mislabelled. If eBay sales are missing, run the order pull
              above first — this panel compares against what has actually been imported.
            </p>
          ) : (
            <div className="space-y-3 mt-5">
              {findings.map((f) => (
                <EbayFindingCard key={f.id} finding={f} busy={busy} onRepair={repair} />
              ))}
            </div>
          )}

          {retag.length > 0 && (
            <div className="mt-6">
              <p className="text-[10px] font-bold tracking-widest text-white/40">
                NOT YET POSTED TO SAGE — SAFE TO FILE AS EBAY SALES
              </p>
              <div className="space-y-2 mt-3">
                {retag.map((o) => (
                  <div
                    key={o.id}
                    className="border border-white/10 p-3 flex items-center gap-3 flex-wrap"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm">{o.customer_name || '—'}</div>
                      <div className="text-xs text-white/50">
                        {money(o.amount)} · {day(o.date)} · filed as {o.source || 'unknown'}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="bg-transparent border-primary/30 text-primary hover:bg-primary/10"
                      disabled={busy === `${o.id}retag`}
                      onClick={() => repair('retag', o.id)}
                    >
                      {busy === `${o.id}retag` ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Tag className="w-3.5 h-3.5" />
                      )}
                      File as eBay sale
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <EbayDismissedFindings
            dismissals={dismissals}
            busy={busy}
            onRestore={(dismissalId) => repair('undismiss', dismissalId, { dismissal_id: dismissalId })}
          />
        </>
      )}
    </div>
  );
}